import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight, Brain, Shield } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Test credentials against API
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`,
        {
          headers: {
            Authorization: "Basic " + btoa(`${credentials.email}:${credentials.password}`),
          },
        }
      );

      if (response.ok) {
        // Store credentials in sessionStorage
        sessionStorage.setItem("adminAuth", btoa(`${credentials.email}:${credentials.password}`));
        sessionStorage.setItem("adminEmail", credentials.email);
        toast.success("Connexion réussie !");
        navigate("/admin/dashboard");
      } else {
        toast.error("Identifiants incorrects");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Erreur de connexion");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#007AFF] flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Administration <span className="text-[#007AFF]">Académie IA</span>
          </h1>
          <p className="text-white/60 text-sm">Espace réservé aux formateurs</p>
        </div>

        {/* Login Form */}
        <div className="glass-card p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#007AFF]" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                placeholder="jb@academie-ia.fr"
                className="mt-2"
                required
                data-testid="input-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#007AFF]" />
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="••••••••"
                className="mt-2"
                required
                data-testid="input-password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="btn-login"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Se connecter
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-center gap-2 text-white/40 text-xs">
            <Shield className="w-4 h-4" />
            Connexion sécurisée
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <a href="/" className="text-white/50 hover:text-white text-sm">
            ← Retour au site
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
